import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { GeminiService } from './gemini.service';

/**
 * Coerces a Gemini JSON reply into a clean list of paragraphs/bullets. The prompts ask for a bare
 * JSON array of strings, but the model doesn't always comply: it may wrap it as
 * {"paragraphs": [...]}, return objects such as [{"text": "..."}], or send one plain string. Used
 * as-is, those shapes made the Step 5 story (and highlight) editors silently show nothing — they
 * render only when the value has a .length — and crashed report generation on .filter()/.trim().
 */
export function toTextList(raw: unknown): string[] {
  if (typeof raw === 'string') {
    return raw.split(/\n\s*\n/).map(s => s.trim()).filter(Boolean);
  }
  if (Array.isArray(raw)) {
    return raw
      .flatMap(item => typeof item === 'string' ? [item]
        : item && typeof item === 'object' ? Object.values(item).filter((v): v is string => typeof v === 'string')
        : [])
      .map(s => s.trim())
      .filter(Boolean);
  }
  if (raw && typeof raw === 'object') {
    const list = Object.values(raw).find(Array.isArray);
    if (list) return toTextList(list);
    return toTextList(Object.values(raw).filter((v): v is string => typeof v === 'string').join('\n\n'));
  }
  return [];
}

export interface NarrativeContext {
  centreName: string;
  periodLabel: string;
}

export interface EducationHighlightsContext extends NarrativeContext {
  teacherSessions: number;
  volunteerSessions: number;
  specialDaysObserved: number;
  childNotes: string;
}

export interface CounsellingHighlightsContext extends NarrativeContext {
  individualSessions: number;
  groupSessions: number;
  concernNotes: string;
}

export interface CaseStudyInput {
  name: string;
  gender: string;
  age: number;
  city: string;
  state: string;
  diagnosis: string;
  hospital: string;
  centreName: string;
  backgroundNotes: string;
}

@Injectable({ providedIn: 'root' })
export class NarrativeImpactReportAiService {

  constructor(private gemini: GeminiService) {}

  async generateEducationHighlights(ctx: EducationHighlightsContext): Promise<string[]> {
    const prompt = `You are writing the "Highlights" bullet section of a quarterly Non-Formal Education Impact Report for Access Life, an NGO supporting children undergoing cancer treatment.

Centre: ${ctx.centreName}
Period: ${ctx.periodLabel}
Session counts: ${ctx.teacherSessions} teacher sessions, ${ctx.volunteerSessions} volunteer sessions, ${ctx.specialDaysObserved} special days observed.

Raw notes from the teacher about specific children and events this quarter:
${ctx.childNotes || '(no notes provided — write generic but warm highlights about engagement, creativity and academic progress)'}

Write 4–6 bullet points. Each bullet is 2–4 sentences describing either an individual child's personality/progress (using their real first name if given in the notes) or a special-day activity that was celebrated. Use "**Name**" markdown-bold around a child's name the first time it appears in a bullet about them.

Respond ONLY with a JSON array of strings, one string per bullet point. No other text.`;

    return toTextList(await firstValueFrom(this.gemini.generateJSON<unknown>(prompt)));
  }

  async generateCounsellingHighlights(ctx: CounsellingHighlightsContext): Promise<string[]> {
    const prompt = `You are writing the "Highlights" bullet section of a quarterly Counselling Impact Report for Access Life, an NGO supporting children undergoing cancer treatment.

Centre: ${ctx.centreName}
Period: ${ctx.periodLabel}
Session counts: ${ctx.individualSessions} individual sessions, ${ctx.groupSessions} group sessions.

Raw notes on concerns/interventions this quarter:
${ctx.concernNotes || '(no notes provided — write generic but accurate highlights about the standard counselling workflow)'}

Write exactly 4 bullet points covering, in order: (1) admission counselling to build rapport and understand mental well-being, (2) regular intermediate assessments to monitor progress, (3) happy discharge counselling with coping strategies for families completing treatment, (4) follow-up counselling support for families visiting for follow-up treatment. Each bullet 1–2 sentences.

Respond ONLY with a JSON array of strings, one string per bullet point. No other text.`;

    return toTextList(await firstValueFrom(this.gemini.generateJSON<unknown>(prompt)));
  }

  async generateProblemsAddressedParagraph(ctx: CounsellingHighlightsContext): Promise<string> {
    const prompt = `Write ONE paragraph (90–130 words) for the "Problems Addressed in Counselling" section of a quarterly Access Life counselling impact report for ${ctx.centreName}, period ${ctx.periodLabel}.

Raw notes on concerns raised by families this quarter:
${ctx.concernNotes || '(no notes provided — cover typical concerns: anxiety about diagnosis/prognosis, caregiving stress, children\'s eating habits, behavioural changes, adjusting to centre living)'}

Tone: warm, professional, evidence-based. Output plain prose, no headings, no bullet points, no markdown, no quotation marks around the whole thing.`;

    return firstValueFrom(this.gemini.generateContent(prompt));
  }

  async generateCaseStudy(input: CaseStudyInput): Promise<string[]> {
    const prompt = `Write a "Braveheart" case study for a quarterly Access Life NGO impact report. Access Life provides shelter, nutrition, education, counselling, and transport to children undergoing cancer treatment and their families, close to the treating hospital.

Child details:
- Name: ${input.name}
- Gender: ${input.gender}
- Age: ${input.age} years
- City/State: ${input.city}, ${input.state}
- Diagnosis: ${input.diagnosis}
- Hospital: ${input.hospital}
- Centre: ${input.centreName}
- Background notes: ${input.backgroundNotes || 'a child receiving treatment who found stability and support at the centre'}

Write 3–4 short paragraphs (60–90 words each) telling ${input.name}'s story: their life/dream before diagnosis, the diagnosis and journey to treatment, how Access Life supported the family, and where things stand now (in treatment, or a hopeful discharge/outlook). Use ${input.name}'s name throughout. Warm, human, specific — not generic.

Respond ONLY with a JSON array of strings, one string per paragraph. No other text.`;

    return toTextList(await firstValueFrom(this.gemini.generateJSON<unknown>(prompt)));
  }
}
